import { useCallback, useState, useEffect } from 'react';
import { router } from '@inertiajs/react';
import { route } from '../../utils/route';
import type { CategoryWithCount } from '../../types/products';
import { CategoryIcon } from './categoryIcons';

interface CategoryTabsProps {
  categories: CategoryWithCount[];
  activeCategoryId: number;
}

export function CategoryTabs({ categories, activeCategoryId }: CategoryTabsProps) {
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);

  // Clear pending state when navigation completes
  useEffect(() => {
    setPendingSlug(null);
  }, [activeCategoryId]);

  const navigate = useCallback((slug: string) => {
    setPendingSlug(slug);
    router.visit(route('products.index', { cat: slug }), { preserveScroll: false });
  }, []);

  return (
    <>
      {/* Desktop: scrollable tab bar */}
      <div className="hidden sm:block mb-4 overflow-x-auto">
        <div className="flex gap-2 pb-1">
          {categories.map(cat => {
            const isActive = pendingSlug ? cat.slug === pendingSlug : cat.id === activeCategoryId;
            return (
            <button
              key={cat.id}
              type="button"
              onClick={() => navigate(cat.slug)}
              className={`whitespace-nowrap rounded-lg px-4 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                isActive
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <CategoryIcon slug={cat.slug} size={14} strokeWidth={2} className="opacity-70" />
              {cat.name}
              {cat.badge && (
                <span className="ml-1.5 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                  {cat.badge}
                </span>
              )}
              {cat.is_new && (
                <span className="ml-1.5 rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-600">
                  nuevo!
                </span>
              )}
            </button>
            );
          })}
        </div>
      </div>

      {/* Mobile: hidden — replaced by MobileToolbar floating bar */}
      <div className="hidden">
        <select
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
          value={activeCategoryId}
          onChange={(e) => {
            const cat = categories.find(c => c.id === Number(e.target.value));
            if (cat) navigate(cat.slug);
          }}
        >
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>
              {cat.name} ({cat.product_count})
              {cat.badge ? ` — ${cat.badge}` : ''}
              {cat.is_new ? ' — nuevo!' : ''}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}
